import { kpisFor } from './kpis'

/*
  What to ask this owner for next.

  The product is worth having because it answers for a specific business. Strip the profile
  out and five model calls come back with what any competent stranger would say about any
  company, which is the thing an owner can already get for free. So an incomplete profile is
  not a cosmetic gap in a settings screen: it is the difference between the product working
  and the product being a chatbot with avatars.

  Two decisions worth keeping.

  **One ask at a time.** A list of eight missing fields is a chore and gets ignored. One
  question with a reason attached gets answered, and the next one appears afterwards.

  **Ordered by what it changes, not by how easy it is.** What the business does reshapes
  every answer the board gives; the fiscal year end changes almost nothing until there are
  figures to date. Asking cheapest-first would fill the profile with the parts that matter
  least and stall on the parts that matter.
*/

export interface Profile {
  headcount: number | null
  annualRevenue: number | null
  ebitda: number | null
  founderSummary: string | null
  website: string | null
  gbpUrl: string | null
  fiscalYearEnd: string | null
  trade: string | null
  completeness: number
}

export type AskScreen = 'company' | 'numbers'

export interface Ask {
  /** Stable across sessions: it is what a snooze is remembered against. */
  id: string
  label: string
  why: string
  screen: AskScreen
  cta: string
}

const ESSENTIALS: { id: string; label: string; why: string; screen: AskScreen; cta: string;
                    missing: (p: Profile) => boolean }[] = [
  {
    id: 'summary',
    label: 'Tell the board what the business actually does',
    why: 'Two or three sentences. It is the single thing that separates advice about your '
       + 'business from advice about businesses.',
    screen: 'company',
    cta: 'Add it',
    missing: p => !p.founderSummary?.trim(),
  },
  {
    id: 'revenue',
    label: 'Add your annual revenue',
    why: 'Every recommendation scales with it. Without it the board hedges, because the '
       + 'right answer at 400k and at 4m are different answers.',
    screen: 'company',
    cta: 'Add it',
    missing: p => p.annualRevenue === null,
  },
  {
    id: 'headcount',
    label: 'Add your headcount',
    why: 'Advice about hiring, pay and span of control is guesswork until the board knows '
       + 'how many people you carry.',
    screen: 'company',
    cta: 'Add it',
    missing: p => p.headcount === null || p.headcount === 0,
  },
  {
    id: 'ebitda',
    label: 'Add your EBITDA',
    why: 'What the business earns before financing decides what it can afford. A board '
       + 'advising on a raise without it is guessing.',
    screen: 'company',
    cta: 'Add it',
    missing: p => p.ebitda === null,
  },
  {
    id: 'statement',
    label: 'Send last year’s profit and loss',
    why: 'One upload fills in most of the above at once, and the board reads the figures '
       + 'rather than being told about them.',
    screen: 'company',
    cta: 'Upload it',
    // Asked only once the obvious fields are in: offering it first is asking somebody to
    // go and find paperwork before they have seen anything work.
    missing: p => p.annualRevenue !== null && p.ebitda === null,
  },
]

/** Everything still outstanding, most valuable first. */
export function asksFor(profile: Profile | null): Ask[] {
  if (!profile) return []

  const out: Ask[] = ESSENTIALS
    .filter(e => e.missing(profile))
    .map(({ missing, ...ask }) => ask)

  // The trade's own numbers come after the universal ones. They are worth more to a board
  // than a fiscal year end, but only once it knows what the business is and how big it is.
  if (out.length === 0) {
    const set = kpisFor(profile.trade)
    const label = set.label ? `a ${set.label}` : 'a board'
    out.push({
      id: 'kpis',
      label: `Add the numbers ${label} runs on`,
      why: `${set.kpis.slice(0, 3).map(k => k.name).join(', ')} and the rest. The board `
         + 'can only reconcile figures it has been given.',
      screen: 'numbers',
      cta: 'See the list',
    })
  }

  return out
}

/**
 * The one to put in front of them, or null when the profile is complete enough or the
 * outstanding ask has been snoozed.
 */
export function nextAsk(profile: Profile | null, isSnoozed: (id: string) => boolean): Ask | null {
  return asksFor(profile).find(a => !isSnoozed(a.id)) ?? null
}
